import { BehaviorSubject, defer, EMPTY, Observable } from 'rxjs';
import { DIALOG_DATA, DialogService } from '../../../../../services/dialog.service';
import { NotificationService } from '../../../../../services/notification.service';
import { ChecklistsService } from '../../../../../services/checklist.service';
import { blank, data_get, data_is_true, fallback, filled, observe, OptionalValue } from '../../../../../shared/utils/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Checklist, ChecklistType } from '../../..';
import { filter, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'checklists-generate-with-ai-dialog',
  template: `
    <fieldmagic-dialog
      label="Generate with AI"
      icon="magic"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          [isInProgress]="processing$ | async"
          label="Generate"
          icon="magic"
          (click)="onGenerate()"
        >
        </fieldmagic-primary-button>

        <fieldmagic-dialog-close-button
          [instance]="self"
          [disabled]="processing$ | async"
        >
        </fieldmagic-dialog-close-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-textarea-input
          [standalone]="false"
          label="Tell me more about the checklist to be generated (This is optional)."
          [(ngModel)]="specifics"
          placeholder="Like standards, etc.."
        >
        </fieldmagic-textarea-input>
      </ng-template>
    </fieldmagic-dialog>
  `
})
export class ChecklistsGenerateWithArtificialIntelligenceDialogComponent implements OnInit {
  specifics?: string;

  readonly processing$ = new BehaviorSubject<boolean>(false);

  get self() {
    return this;
  }

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: ChecklistsGenerateWithArtificialIntelligenceDialogData,
    private readonly _notifications: NotificationService,
    private readonly _dialog: DialogService,
    private readonly _checklists: ChecklistsService,
  ) {}

  ngOnInit(): void {
    if (filled(data_get(this._data, 'specifics'))) {
      this.specifics = this._data.specifics;
    }
  }

  onGenerate = () => observe({
    before: () => this.processing$.next(true),
    after: () => this.processing$.next(false),
    observable: () => this._checklists.generate({
      checklist_type: this._data.checklist_type,
      checklist_name: this._data.checklist_name,
      asset_type_id: this._data.asset_type_id,
      asset_type_name: this._data.asset_type_name,
      specifics: this.specifics,
      preview: this._data.preview,
    }),
  })
  .pipe(
    tap((checklist) => blank(checklist) && this._notifications.notifyError('Unable to generate checklist. Please try again')),
    filter(filled),
  )
  .subscribe((checklist) => this._dialog.close({
    instance: this,
    result: checklist,
  }));
}

export type ChecklistsGenerateWithArtificialIntelligenceDialogData = {
  checklist_type: ChecklistType;
  checklist_name?: string;
  asset_type_id?: string;
  asset_type_name?: string;
  preview?: boolean;
  specifics?: string;
};

export type ChecklistsGenerateWithArtificialIntelligenceDialogResult = Checklist;

@Component({
  selector: `checklists-generate-with-ai-button`,
  template: `
    <fieldmagic-primary-button
      purpose="ai"
      [isInProgress]="generating$ | async"
      (click)="onGenerate()"
    >
    </fieldmagic-primary-button>
  `,
  providers: [
    DialogService,
  ],
})
export class ChecklistsGenerateWithArtificationIntelligenceButtonComponent {
  @Input() data: ChecklistsGenerateWithArtificationIntelligenceButtonData;

  @Output('generated') $onGenerate = new EventEmitter<Checklist>();

  readonly generating$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly _dialog: DialogService,
    private readonly _notifications: NotificationService,
    private readonly _translator: TranslateService,
  ) {}

  onGenerate = () => observe({
    before: () => this.generating$.next(true),
    after: () => this.generating$.next(false),
    observable: () => defer(() => {
      if (! this._isValid()) {
        return EMPTY;
      }

      const mode = fallback(this.data.mode, {
        fallback: () => 'add',
      });

      if (mode == 'edit') {
        return this._notifications.sendConfirmation('This will override the current checklist prompts. Are you sure you wish to continue?').pipe(
          filter((confirmation) => confirmation.answer),
          switchMap(() => this._showDialog()),
        );
      }

      return this._showDialog();
    }),
  })
    .pipe(
      filter(filled),
      tap(() => this._notifications.notifySuccess('Checklist successfully generated.')),
    )
    .subscribe((checklist) => {
      let output: Checklist = {
        ... checklist,
      };

      this.$onGenerate.emit(output);
    });

  private _isValid(): boolean {
    const values = {
      ... this.data,
    };

    if (blank(data_get(values, 'checklist_type'))) {
      this._notifications.notifyError('Please make sure to select what type of checklist to generate.');
      return false;
    }

    if (blank(data_get(values, 'name'))
      && data_get(values, 'checklist_type') != 'asset'
    ) {
      this._notifications.notifyError('Please provide a checklist name.');
      return false;
    }

    if (data_get(values, 'checklist_type') == 'asset'
      && blank(data_get(values, 'asset_type_id'))
      && blank(data_get(values, 'asset_type_name'))
    ) {
      this._notifications.notifyError('Please specify the asset type name.');
      return false;
    }

    return true;
  }

  private _showDialog = () => new Observable<OptionalValue<ChecklistsGenerateWithArtificialIntelligenceDialogResult>>((observer) => {
    this._dialog.show<ChecklistsGenerateWithArtificialIntelligenceDialogComponent, ChecklistsGenerateWithArtificialIntelligenceDialogData>({
      component: ChecklistsGenerateWithArtificialIntelligenceDialogComponent,
      data: {
        checklist_type: data_get(this.data, 'checklist_type'),
        asset_type_id: data_get(this.data, 'asset_type_id'),
        checklist_name: data_get(this.data, 'name'),
        preview: data_get(this.data, 'mode') == 'edit'
          || data_is_true(data_get(this.data, 'force_preview')),
        specifics: data_get(this.data, 'specifics'),
        asset_type_name: data_get(this.data, 'asset_type_name'),
      },
      afterClosing: (checklist) => observer.next(checklist),
    });
  });
}

export type ChecklistsGenerateWithArtificationIntelligenceButtonData = {
  checklist_type: ChecklistType;
  asset_type_id?: string;
  asset_type_name?: string;
  name?: string;
  mode?: 'edit' | 'add';
  specifics?: string;
  force_preview?: boolean;
}

export type ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent = Checklist;